import AssetUpload from 'components/AssetUpload';
import { COURSE_ICONS, COURSE_ICONS_PATH } from 'constants/course-icons';
import {
  Col,
  Divider,
  Flex,
  Form,
  FormProps,
  Image,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Typography
} from 'antd';
import { IProductCategory } from 'interfaces/product_categories';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelect } from '@refinedev/antd';
import type { IGraduateCourseShow } from 'interfaces/graduate_courses';

interface TabGeneralInfoProps {
  formProps: FormProps<IGraduateCourseShow>;
  record?: IGraduateCourseShow;
}

export const TabGeneralInfo = ({ formProps, record }: TabGeneralInfoProps) => {
  const [courseType, setCourseType] = useState<'regular' | 'on_demand'>(
    record?.course_type || 'regular'
  );

  useEffect(() => {
    if (record?.course_type) {
      setCourseType(record.course_type);
    }
  }, [record?.course_type]);

  const { selectProps: productCategorySelectProps } =
    useSelect<IProductCategory>({
      resource: 'products/categories',
      optionLabel: 'name',
      optionValue: 'name',

      onSearch: value => [
        {
          field: 'filter[by_name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server'
      }
    });

  const kind = useMemo(
    () => formProps.initialValues?.kind || 'graduate',
    [formProps.initialValues?.kind]
  );

  const icon = useMemo(
    () => formProps.initialValues?.media?.icon || COURSE_ICONS[2],
    [formProps.initialValues?.media?.icon]
  );

  const onChangeIcon = useCallback(
    (e: RadioChangeEvent) => {
      const value = e.target.value;
      formProps.form?.setFieldValue(['media', 'icon'], value);
    },
    [formProps.form]
  );

  return (
    <Form {...formProps} layout="vertical">
      <Form.Item
        label="Nome"
        name="name"
        rules={[
          {
            required: true
          }
        ]}
      >
        <Input />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item label="ID do curso" name="id">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Tipo"
            name="kind"
            rules={[
              {
                required: true
              }
            ]}
          >
            <Select disabled>
              <Select.Option value="graduate">Pós Graduação</Select.Option>
              <Select.Option value="undergraduate">Graduação</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Código"
            name="code"
            rules={[
              {
                required: Boolean(formProps.initialValues?.code),
                message: 'O código é obrigatório.'
              },
              {
                max: 4,
                message: 'O código deve ter apenas 4 digitos'
              }
            ]}
          >
            <Input maxLength={4} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label="Código e-MEC" name="emec">
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Duração (horas)"
            name="duration_in_hours"
            rules={[
              {
                required: true
              }
            ]}
          >
            <InputNumber min={0} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Nota mínima (0 - 100)"
            name="min_evaluation_score"
            rules={[
              {
                required: true
              }
            ]}
          >
            <InputNumber min={0} max={100} />
          </Form.Item>
        </Col>
      </Row>

      {kind === 'undergraduate' && (
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Horas mínimas de AAC"
              name="complementary_activity_minimum_hours"
              tooltip="Atividades Acadêmicas Complementares exigidas do aluno. Vazio conta como 0."
            >
              <InputNumber min={0} precision={0} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {record?.unit_name && (
        <p style={{ marginTop: 10, color: '#595959' }}>
          <strong>Unidade/Campus: </strong>
          {record?.unit_name}
        </p>
      )}

      <Divider />

      <Typography.Title level={5}>Configuração de curso</Typography.Title>
      <Row gutter={16}>
        <Col xs={24} md={6}>
          <Form.Item
            label="Tipo de curso"
            name="course_type"
            initialValue="regular"
            style={{ marginBottom: 8 }}
            rules={[
              {
                required: true
              }
            ]}
          >
            <Radio.Group
              onChange={e => setCourseType(e.target.value)}
              value={courseType}
            >
              <Radio.Button value="regular">Regular</Radio.Button>
              <Radio.Button value="on_demand">Flexível</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>

        {courseType === 'on_demand' && (
          <Col xs={24} md={12}>
            <Form.Item
              label="Dias mínimos para aprovação"
              name="minimum_days_to_approve"
              rules={[
                {
                  required: true,
                  message: 'Campo obrigatório para cursos flexíveis'
                }
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginBottom: 16 }}
      >
        <strong>Regular:</strong> Curso com datas de início e término definidas.
        O certificado é liberado somente após o fechamento de todas as turmas.
        <br />
        <strong>Flexível:</strong> O aluno pode iniciar e concluir o curso no
        seu próprio ritmo. O certificado é liberado após a conclusão do curso,
        respeitando o prazo mínimo configurado.
      </Typography.Text>

      <Divider />

      <Form.Item
        label="Card"
        name={['media', 'card_image']}
        rules={[
          {
            required: false
          }
        ]}
      >
        <AssetUpload
          accept="image/png, image/jpeg"
          type="course"
          src={record?.media?.card_image}
        />
      </Form.Item>

      <Typography.Title level={5}>
        Personalização e categorização
      </Typography.Title>

      <Typography.Text
        className="block text-neutral-500"
        style={{ marginBottom: 16 }}
      >
        Adicione um ícone e etiqueta para representar e classificar seu curso.
        Essas informações ajudam na organização e poderão ser alteradas depois.
      </Typography.Text>

      <Form.Item
        label="Etiquetas"
        name={['media', 'tags']}
        extra={
          <Typography.Text type="secondary" className="block mt-2">
            Para editar as etiquetas entre em Loja Online `{'>'}` Categorias
          </Typography.Text>
        }
      >
        <Select
          {...productCategorySelectProps}
          placeholder="Selecione uma ou mais etiquetas..."
          mode="multiple"
        />
      </Form.Item>

      <Form.Item name={['media', 'icon']} label="Ícone" initialValue={icon}>
        <Typography.Text
          type="secondary"
          className="block text-secondary-500 mt-0 mb-4"
        >
          Escolha o ícone que irá aparecer no card da tela de cursos do aluno
        </Typography.Text>

        <Radio.Group
          rootClassName="course-icons-radio-wrapper"
          onChange={onChangeIcon}
          defaultValue={icon}
          options={COURSE_ICONS.map((icon, index) => ({
            value: icon,
            label: (
              <Flex vertical gap={4}>
                <Image
                  preview={false}
                  src={`${COURSE_ICONS_PATH}/${icon}`}
                  alt={icon}
                  width={50}
                  height={50}
                />
                {((kind === 'graduate' && index === 2) ||
                  (kind === 'undergraduate' && index === 1)) && (
                  <Typography.Text type="secondary">Padrão</Typography.Text>
                )}
              </Flex>
            )
          }))}
        />
      </Form.Item>
    </Form>
  );
};
